import { useCallback, useRef } from 'react';

import { TertiaryButton } from '@changegps/horizon-component-library';
import InfoIcon from '@mui/icons-material/Info';
import { Alert, Stack, Tooltip, Typography } from '@mui/material';

interface ImageSectionProps {
  title: string;
  tooltip?: string;
  imageUrl?: string;
  onUpload: (file: File) => void;
  onDelete: () => void;
  isLoading?: boolean;
  error?: string;
  accept: string;
}

export const ImageSection = ({
  title,
  tooltip,
  imageUrl,
  onUpload,
  onDelete,
  isLoading,
  error,
  accept,
}: ImageSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onUpload(file);
      }
      // Reset input value to allow re-uploading the same file
      event.target.value = '';
    },
    [onUpload],
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems={'center'} spacing={1} mb={2}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          {title}
        </Typography>
        <Tooltip
          title={tooltip}
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                whiteSpace: 'pre-line',
              },
            },
          }}
        >
          <InfoIcon fontSize="small" />
        </Tooltip>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ width: '200px', mb: 2 }}>
          {error}
        </Alert>
      )}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          style={{
            border: '2px solid #EFEFEF',
            width: '200px',
            height: '200px',
            borderRadius: '10px',
            objectFit: 'cover',
          }}
        />
      ) : (
        <Typography
          sx={{
            width: '200px',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #ccc',
            borderRadius: '10px',
            color: '#888',
          }}
        >
          No Image Uploaded
        </Typography>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Stack direction={'row'} spacing={1} sx={{ width: '200px' }}>
        <TertiaryButton
          fullWidth
          onClick={handleUploadClick}
          disabled={isLoading}
        >
          Upload
        </TertiaryButton>
        <TertiaryButton
          fullWidth
          onClick={onDelete}
          disabled={isLoading || !imageUrl}
        >
          Delete
        </TertiaryButton>
      </Stack>
    </Stack>
  );
};
